import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Download, Filter, FileText, Eye, X, CheckCircle, XCircle } from "lucide-react";
import { supabase } from "../../../../src/integrations/supabase/client";
import { PDFViewer } from "@/components/ui/pdf-viewer";
import { useToast } from "@/hooks/use-toast";

interface ArticlesManagerProps {
  userRegion?: string;
  isGlobalAdmin: boolean;
}

interface Article {
  id: string;
  title: string;
  author_name: string;
  author_email: string;
  file_path: string;
  file_name: string;
  file_type: string;
  status: string;
  admin_notes: string | null;
  created_at: string;
  region_id: string;
  regions: { name: string } | null;
}

export const ArticlesManager: React.FC<ArticlesManagerProps> = ({ userRegion, isGlobalAdmin }) => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [regions, setRegions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState<Article | null>(null);
  const [editForm, setEditForm] = useState({ status: "", admin_notes: "" });
  const [filters, setFilters] = useState({
    region: "all",
    startDate: "",
    endDate: "",
    status: "all",
  });
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingArticleUrl, setViewingArticleUrl] = useState<string>("");
  const [viewingArticleName, setViewingArticleName] = useState<string>("");
  const [viewingIsPdf, setViewingIsPdf] = useState<boolean>(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchRegions();
    fetchArticles();
  }, [filters]);

  const fetchRegions = async () => {
    const { data, error } = await supabase.from("regions").select("id, name").order("name");

    if (error) {
      console.error("Error fetching regions:", error);
    } else {
      setRegions(data || []);
    }
  };

  const fetchArticles = async () => {
    setLoading(true);

    let query = supabase
      .from("submitted_articles")
      .select(
        `
        *,
        regions (name)
      `,
      )
      .order("created_at", { ascending: false });

    // Apply filters
    if (filters.region !== "all") {
      query = query.eq("region_id", filters.region);
    }

    if (filters.startDate) {
      const startDateTime = `${filters.startDate}T00:00:00.000Z`;
      query = query.gte("created_at", startDateTime);
    }

    if (filters.endDate) {
      const endDateTime = `${filters.endDate}T23:59:59.999Z`;
      query = query.lte("created_at", endDateTime);
    }

    if (filters.status !== "all") {
      query = query.eq("status", filters.status);
    }

    // Regional admins can only see their region's articles
    if (!isGlobalAdmin && userRegion) {
      query = query.eq("region_id", userRegion);
    }

    const { data, error } = await query;

    if (error) {
      console.error("Error fetching articles:", error);
      toast({
        title: "Error",
        description: "Failed to fetch articles",
        variant: "destructive",
      });
    } else {
      setArticles(data || []);
    }

    setLoading(false);
  };

  const downloadArticle = async (article: Article) => {
    try {
      const { data, error } = await supabase.storage.from("general-media").download(article.file_path);

      if (error) throw error;

      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = article.file_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: "Success",
        description: "Article downloaded successfully",
      });
    } catch (error) {
      console.error("Error downloading article:", error);
      toast({
        title: "Error",
        description: "Failed to download article",
        variant: "destructive",
      });
    }
  };

  const viewArticle = async (article: Article) => {
    try {
      const { data, error } = await supabase.storage.from("general-media").createSignedUrl(article.file_path, 3600);

      if (error) throw error;

      if (data?.signedUrl) {
        const isPdf = article.file_type === "application/pdf" || /\.pdf($|\?)/i.test(article.file_name);
        setViewingArticleUrl(data.signedUrl);
        setViewingArticleName(article.file_name || `${article.title}.pdf`);
        setViewingIsPdf(isPdf);
        setViewDialogOpen(true);
      } else {
        throw new Error("Failed to generate signed URL");
      }
    } catch (error) {
      console.error("Error viewing article:", error);
      toast({
        title: "Error",
        description: "Failed to view article. Please try downloading it instead.",
        variant: "destructive",
      });
    }
  };

  const handleEditStatus = (article: Article) => {
    setEditingArticle(article);
    setEditForm({
      status: article.status,
      admin_notes: article.admin_notes || "",
    });
    setEditDialogOpen(true);
  };

  const handleUpdateArticle = async () => {
    if (!editingArticle) return;

    try {
      const { error } = await supabase
        .from("submitted_articles")
        .update({
          status: editForm.status,
          admin_notes: editForm.admin_notes,
        })
        .eq("id", editingArticle.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Article updated successfully",
      });

      setEditDialogOpen(false);
      fetchArticles();
    } catch (error) {
      console.error("Error updating article:", error);
      toast({
        title: "Error",
        description: "Failed to update article",
        variant: "destructive",
      });
    }
  };

  const handleStatusChange = async (article: Article, newStatus: string) => {
    try {
      const { error } = await supabase.from("submitted_articles").update({ status: newStatus }).eq("id", article.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Article ${newStatus}`,
      });

      fetchArticles();
    } catch (error) {
      console.error("Error updating status:", error);
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive",
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "approved":
        return "default";
      case "pending":
        return "secondary";
      case "rejected":
        return "destructive";
      default:
        return "outline";
    }
  };

  const getFileTypeColor = (fileType: string) => {
    switch (fileType) {
      case "application/pdf":
        return "bg-red-100 text-red-800";
      case "application/msword":
      case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        return "bg-blue-100 text-blue-800";
      case "text/plain":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-purple-100 text-purple-800";
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={filters.status} onValueChange={(value) => setFilters({ ...filters, status: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Region</Label>
              <Select value={filters.region} onValueChange={(value) => setFilters({ ...filters, region: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="All Regions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Regions</SelectItem>
                  {regions.map((region) => (
                    <SelectItem key={region.id} value={region.id}>
                      {region.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>End Date</Label>
              <Input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Articles ({articles.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading articles...</div>
          ) : articles.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No articles found matching the current filters.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Author</TableHead>
                    <TableHead>Region</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Submitted</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {articles.map((article) => (
                    <TableRow key={article.id}>
                      <TableCell className="font-medium max-w-xs truncate">{article.title}</TableCell>
                      <TableCell>{article.author_name}</TableCell>
                      <TableCell>{article.regions?.name || "N/A"}</TableCell>
                      <TableCell>
                        <Badge variant={getStatusColor(article.status)}>{article.status}</Badge>
                      </TableCell>
                      <TableCell>{new Date(article.created_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" onClick={() => viewArticle(article)} title="View">
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => downloadArticle(article)} title="Download">
                            <Download className="h-4 w-4" />
                          </Button>
                          {article.status !== "approved" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleStatusChange(article, "approved")}
                              title="Approve"
                            >
                              <CheckCircle className="h-4 w-4 text-green-600" />
                            </Button>
                          )}
                          {article.status !== "rejected" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleStatusChange(article, "rejected")}
                              title="Reject"
                            >
                              <XCircle className="h-4 w-4 text-red-600" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Status Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Article Status</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Status</Label>
              <Select value={editForm.status} onValueChange={(value) => setEditForm({ ...editForm, status: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Admin Notes</Label>
              <Textarea
                value={editForm.admin_notes}
                onChange={(e) => setEditForm({ ...editForm, admin_notes: e.target.value })}
                rows={4}
                placeholder="Add notes for internal use..."
              />
            </div>
            <div className="flex justify-start gap-2">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateArticle}>Save Changes</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* PDF / Article Viewer Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-[100vw] sm:max-w-5xl w-full h-[100dvh] sm:h-[90vh] p-0 gap-0 flex flex-col overflow-hidden">
          <DialogHeader className="px-4 py-2 border-b shrink-0">
            <DialogTitle className="truncate text-sm sm:text-base">{viewingArticleName || "Article"}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-hidden">
            {viewingArticleUrl && viewingIsPdf ? (
              <PDFViewer
                url={viewingArticleUrl}
                fileName={viewingArticleName}
                dialogOpen={viewDialogOpen}
                debugLabel="Admin Article PDF Viewer"
              />
            ) : viewingArticleUrl ? (
              <iframe src={viewingArticleUrl} title={viewingArticleName} className="w-full h-full border-0" />
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
